import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useNotificationStateContext } from '../context/NotificationStateContext';
import { 
  saveNotificationPreferences, 
  loadNotificationPreferences, 
  checkBackendNotificationStatus,
  validateAndCleanPreferences
} from '../utils/notificationUtils';
import { headerService } from '../services/headers';
import { API_CONFIG } from '../config/api';

export const useNotificationStateManager = () => {
  const { user } = useAuth();
  const { isActive, setActive, refreshStatus } = useNotificationStateContext();
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Log del estado recibido del contexto
  console.log('🔔 useNotificationStateManager - Estado recibido del contexto:', { 
    isActive, 
    userId: user?.uid,
    timestamp: new Date().toISOString()
  });

  // Inicializar estado al montar el componente
  useEffect(() => {
    if (user?.uid && !isInitialized) {
      // Primero validar y limpiar localStorage corrupto
      validateAndCleanPreferences(user.uid);
      initializeNotificationState();
    }
  }, [user?.uid, isInitialized]);

  // Función para inicializar el estado de notificaciones
  const initializeNotificationState = useCallback(async () => {
    if (!user?.uid) return;

    try {
      console.log('🚀 Inicializando estado de notificaciones...');
      
      // Cargar estado desde localStorage
      const cachedPrefs = loadNotificationPreferences(user.uid);
      if (cachedPrefs) {
        console.log('📱 Estado inicial desde localStorage:', cachedPrefs.notificationsEnabled);
        setActive(cachedPrefs.notificationsEnabled);
      }
      
      // Verificar estado real del backend
      const backendStatus = await checkBackendNotificationStatus(
        user.uid, 
        API_CONFIG.BASE_URL, 
        headerService.getHeaders('authenticated')
      );
      
      console.log('✅ Estado real del backend:', backendStatus);
      
      // Actualizar estado con valor del backend
      setActive(backendStatus);
      
      // Sincronizar localStorage si es necesario
      if (cachedPrefs?.notificationsEnabled !== backendStatus) {
        console.log('🔄 Sincronizando localStorage con backend...');
        saveNotificationPreferences(backendStatus, user.uid);
      }
      
      setLastSyncTime(new Date());
      setIsInitialized(true);
      console.log('✅ Estado de notificaciones inicializado');
      
    } catch (error) {
      console.error('❌ Error inicializando estado de notificaciones:', error);
      
      // Usar localStorage como fallback
      const cachedPrefs = loadNotificationPreferences(user.uid);
      if (cachedPrefs) {
        console.log('📱 Usando localStorage como fallback:', cachedPrefs.notificationsEnabled);
        setActive(cachedPrefs.notificationsEnabled);
      }
      
      setIsInitialized(true);
    }
  }, [user?.uid, setActive]);

  // Función para sincronizar estado con backend
  const syncWithBackend = useCallback(async () => {
    if (!user?.uid) return;

    try {
      console.log('🔄 Sincronizando estado con backend...');
      
      const backendStatus = await checkBackendNotificationStatus(
        user.uid, 
        API_CONFIG.BASE_URL, 
        headerService.getHeaders('authenticated')
      );
      
      console.log('✅ Estado del backend:', backendStatus);
      
      // Actualizar estado
      setActive(backendStatus);
      
      // Actualizar localStorage
      saveNotificationPreferences(backendStatus, user.uid);
      
      setLastSyncTime(new Date());
      console.log('✅ Estado sincronizado con backend');
      
    } catch (error) {
      console.error('❌ Error sincronizando con backend:', error);
      throw error;
    }
  }, [user?.uid, setActive]);

  // Función para actualizar estado local
  const updateLocalState = useCallback((newState: boolean) => {
    if (!user?.uid) return;

    console.log('🔔 updateLocalState llamado con:', newState);
    console.log('🔔 Estado anterior:', isActive);
    console.log('🔔 Stack trace:', new Error().stack?.split('\n').slice(1, 4).join('\n'));
    
    setActive(newState);
    
    console.log('🔔 setActive llamado, esperando actualización...');
    
    saveNotificationPreferences(newState, user.uid);
    setLastSyncTime(new Date());
    
    console.log('🔔 updateLocalState completado');
  }, [user?.uid, setActive, isActive]);

  // Función para refrescar estado completo
  const refreshState = useCallback(async () => {
    if (!user?.uid) return;

    try {
      console.log('🔄 Refrescando estado completo...');
      await refreshStatus();
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('❌ Error refrescando estado:', error);
      throw error;
    }
  }, [user?.uid, refreshStatus]);

  return {
    isActive,
    isInitialized,
    lastSyncTime,
    updateLocalState,
    syncWithBackend,
    refreshState,
    initializeNotificationState
  };
};
